<template>
  <div class="options-chooser-field">
    <!-- The outer VInput is what VForm validates: it carries the field's own
         rules (`validateOnePerSection`, bound at Save like every other field)
         against the whole flat value, so a user who never opens the window
         still cannot save without the required picks. -->
    <v-input
      ref="input"
      :model-value="modelValue"
      :rules="rules"
      :disabled="disabled"
      :readonly="readonly"
      hide-details="auto"
    >
      <div class="options-chooser-field__body">
        <div class="options-chooser-field__label text-medium-emphasis">
          {{ label }}
        </div>
        <div class="options-chooser-field__picks">
          <v-chip v-for="pick in picks" :key="getOptionUid(pick)" size="small">
            {{ getOptionDescr(pick) }}
          </v-chip>
          <!-- Tinted, so it never reads as one more chip beside the picks.
               `fieldfocus` rather than `primary`: the same purple in light, but
               purple-200 in dark, where primary is too dim for small text (see
               the token in nuxt.config.ts). -->
          <v-btn
            size="small"
            variant="tonal"
            color="fieldfocus"
            :rounded="true"
            :disabled="disabled || readonly"
            @click="open"
          >
            Choose Options...
          </v-btn>
        </div>
      </div>
    </v-input>

    <!-- A stock EntityMaskForm in its own window, one select per choice (see
         cynqueModules/optionSections.ts). Mirrors the subentity EntityDialog:
         same overlay, persistent, Escape cancels -- but WIDER than the entity
         dialog it opens from, by the margin the subentity dialog is narrower.
         The fields are rebuilt on every open from the current picks, so
         Cancel discards cleanly. -->
    <v-dialog
      v-model="isOpen"
      :max-width="DIALOG_MAX_WIDTH"
      :overlay-opacity="overlayOpacity"
      persistent
      @keydown.esc="cancel"
    >
      <EntityMaskForm
        v-if="isOpen"
        class="options-chooser-form"
        entity-name="option"
        :entity-name-title-override="label"
        entity-name-title-prefix-override=""
        message="Choose the options to include..."
        :do-subdue-message="true"
        :do-align-message-with-title="true"
        save-button-caption-verb-override="Done"
        :input-fields="chooserFields"
        :max-width="DIALOG_MAX_WIDTH"
        :subentity-did-cancel="false"
        :subentity-did-return-value-value="undefined"
        @cancel="cancel"
        @save="done"
      />
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent } from 'vue'
import type { PropType } from 'vue'
import {
  buildOptionSectionInputFields,
  getOptionDescr,
  getOptionItems,
  getOptionUid,
  isOptionPicked,
  readPicksFromOptionFields,
  withLoneRequiredOptionsPicked,
} from '~/cynqueModules/optionSections'
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
import type { TEntityMaskItem } from '~/types/models'

/**
 * A kit's optional items in an Add Services dialog: the picks as chips, and a
 * "Choose Options..." button opening them one select per choice in their own
 * window. Rendered by EntityMaskForm for `type: 'optionsChooser'`.
 *
 * The value is the SAME flat array of picked option objects the old multiple
 * select held, so the specs' `runOnChanged` (`enforceOnePerSection`), the
 * `onePerSection` validator and every consumer of the value are unchanged.
 */
export default defineComponent({
  name: 'OptionsChooserField',
  components: {
    // ⚠ ASYNC, deliberately: EntityMaskForm renders this component, and this
    // component renders EntityMaskForm. A static import makes the two modules
    // import each other; resolving it lazily breaks the cycle.
    EntityMaskForm: defineAsyncComponent(() => import('./EntityMaskForm.vue')),
  },
  props: {
    modelValue: {
      type: Array as PropType<any[] | null>,
      default: null,
    },
    items: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Options',
    },
    rules: {
      type: Array as PropType<any[]>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data: () => ({
    // EntityDialog's 560, plus the 30 its subentity dialog (530) gives up.
    DIALOG_MAX_WIDTH: 590,
    isOpen: false,
    chooserFields: [] as TEntityMaskItem[],
  }),
  computed: {
    // In list order, whatever order the value holds them in.
    picks(): any[] {
      return getOptionItems(this.items).filter((item) =>
        isOptionPicked(this.modelValue, item)
      )
    },
    overlayOpacity(): number {
      return isDarkTheme() ? 0.8 : 0.5
    },
  },
  watch: {
    // ⚠ Watches the VALUE too, not just the items: `clearOtherOnChanged` can
    // null the value while the list stays, and a lone required option must come
    // straight back or the save would fail on a choice nobody was offered.
    items: {
      handler() {
        this.pickLoneRequiredOptions()
      },
      immediate: true,
    },
    modelValue() {
      this.pickLoneRequiredOptions()
    },
  },
  methods: {
    getOptionDescr,
    getOptionUid,
    open() {
      this.chooserFields = buildOptionSectionInputFields({
        items: this.items,
        value: this.modelValue,
        cols: 6,
      })
      this.isOpen = true
    },
    cancel() {
      this.isOpen = false
    },
    // Read the form as it stands rather than the dirty-only payload
    // EntityMaskForm emits: every section's value, changed or not.
    done() {
      const currentValues = Object.fromEntries(
        this.chooserFields.map((field) => [field.name, field.value])
      )
      this.$emit(
        'update:modelValue',
        readPicksFromOptionFields({
          items: this.items,
          fields: this.chooserFields,
          changedValues: currentValues,
        })
      )
      this.isOpen = false
    },
    // EntityMaskForm calls this on the field's ref when `clearOtherOnChanged`
    // clears it, as it does on a VSelect.
    resetValidation() {
      ;(this.$refs.input as any)?.resetValidation?.()
    },
    // Not in view mode: there, picking would edit a record the user is only
    // looking at.
    pickLoneRequiredOptions() {
      if (this.readonly) {
        return
      }
      const next = withLoneRequiredOptionsPicked({
        items: this.items,
        value: this.modelValue,
      })
      if (next) {
        this.$emit('update:modelValue', next)
      }
    },
  },
})
</script>

<style scoped>
.options-chooser-field__body {
  width: 100%;
}

.options-chooser-field__label {
  font-size: 0.75rem;
  margin-bottom: 4px;
}

.options-chooser-field__picks {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* The chooser's "Choose the options to include..." subhead -- its weight and
   colour come from EntityMaskForm's `do-subdue-message`, its left edge from
   `do-align-message-with-title`. EntityMaskForm's root <v-form> carries this
   component's scope id, so :deep() reaches it below.

   LIGHT ONLY, 4px closer under the title: the message block's `mt-3`
   (12px, a !important utility) -> 8px. Everything below moves up with it. */
.v-theme--light .options-chooser-form :deep(.v-card-text.emf-dialog-message) {
  margin-top: 8px !important;
}
/* The FIRST supplier divider -- LIGHT ONLY: `mt-12`'s 48px left a hole under
   the one-line message. 44px instead, which makes the gap from the message's
   text down to this rule equal the gap from the last field's underline down to
   the next supplier's rule (both measured 44px). Marked in optionSections.ts;
   `mt-12` is a !important utility. */
.v-theme--light
  .options-chooser-form
  :deep(.v-divider.emf-first-option-group-divider) {
  margin-top: 44px !important;
}
/* The supplier headings (cynqueModules/optionSections.ts) -- LIGHT ONLY: the
   denser near-black and purple make the rule above sit visually further off and
   the selects below crowd in, most of all once a select is filled and its label
   floats up under the heading. So 6px less above (EntityMaskForm's 16px top
   padding -> 10px) and 8px more below (fieldClass's !important `mb-n6` -24px
   -> -16px). Dark keeps EntityMaskForm's spacing.
   Also a little darker, at the same light weight: VCardSubtitle fades its
   `text-primary` to 0.6 opacity, a pale lavender on white; 0.8 instead
   (composite #C47DD0 -> #B052C0). Dark already runs these at full opacity. */
.v-theme--light
  .options-chooser-form
  :deep(.v-card-subtitle.emf-field-subtitle) {
  padding-top: 10px;
  margin-bottom: -16px !important;
  opacity: 0.8;
}
</style>
